import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Pipeline from "./pages/Pipeline";
import Leads from "./pages/Leads";
import Projects from "./pages/Projects";
import Tools from "./pages/Tools";
import ConsulenzeTecniche from "./pages/ConsulenzeTecniche";
import LeadDetail from "./pages/LeadDetail";
import Products from "./pages/Products";
import Listino from "./pages/Listino";
import Progettazione from "./pages/Progettazione";
import CostiBase from "./pages/CostiBase";
import InverterCosts from "./pages/InverterCosts";
import ManodoperaCosts from "./pages/ManodoperaCosts";
import SicurezzaCosts from "./pages/SicurezzaCosts";
import MarginiCosts from "./pages/MarginiCosts";
import Condizioni from "./pages/Condizioni";
import Pagamenti from "./pages/Pagamenti";
import BonusFiscali from "./pages/BonusFiscali";
import Caparra from "./pages/Caparra";
import Manutenzione from "./pages/Manutenzione";
import Preventivatore from "./pages/Preventivatore";
import FierPreventivi from "./pages/FierPreventivi";
import Orders from "./pages/Orders";
import MotoreBolletta from "./pages/MotoreBolletta";
import ArchivioBollette from "./pages/ArchivioBollette";
import Manutenzioni from "./pages/Manutenzioni";
import ListinoServizi from "./pages/ListinoServizi";
import CondizioniVendita from "./pages/CondizioniVendita";
import SopralluoghiSettings from "./pages/SopralluoghiSettings";
import ObiettiviProspect from "./pages/ObiettiviProspect";
import EmailTemplates from "./pages/EmailTemplates";
import CompanyPhotos from "./pages/CompanyPhotos";
import AutomazioniCRM from "./pages/AutomazioniCRM";
import EmailQueue from "./pages/EmailQueue";
import PartnerCommissions from "./pages/PartnerCommissions";
import SubappaltoDocument from "./pages/SubappaltoDocument";
import Appointments from "./pages/Appointments";
import Agenda from "./pages/Agenda";
import PartnerInstallers from "./pages/PartnerInstallers";
import AgenzieAbilitate from "./pages/AgenzieAbilitate";
import HeadPartnerBoard from "./pages/HeadPartnerBoard";
import LegalTexts from "./pages/LegalTexts";
import SubappaltoArticoli from "./pages/SubappaltoArticoli";
import Profile from "./pages/Profile";
import OutlookPrint from "./pages/OutlookPrint";
import PublicOutlook from "./pages/PublicOutlook";
import PublicInstallersMap from "./pages/PublicInstallersMap";
import FreeBolletta from "./pages/FreeBolletta";
import FreeTetto from "./pages/FreeTetto";
import FreeColonnina from "./pages/FreeColonnina";
import FreeUsers from "./pages/FreeUsers";
import PublicConsulenza from "./pages/PublicConsulenza";
import RetePage from "./pages/RetePage";
import RetePratica from "./pages/RetePratica";
import ConfiguraWizard from "./pages/ConfiguraWizard";
import PrivacyPage from "./pages/PrivacyPage";
import ProtectedContent from "./components/ProtectedContent";
import ErrorBoundary from "./components/ErrorBoundary";

function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname + location.search}>{children}</ErrorBoundary>;
}
import PaymentResult from "./pages/PaymentResult";
import Settings from "./pages/Settings";
import StripeTest from "./pages/StripeTest";
import AreaPartner from "./pages/AreaPartner";
import AreaPartnerLead from "./pages/AreaPartnerLead";
import QualitaCRM from "./pages/QualitaCRM";
import Prospects from "./pages/Prospects";
import ProspectDetail from "./pages/ProspectDetail";
import ProspectDashboard from "./pages/ProspectDashboard";
import PipelineProspect from "./pages/PipelineProspect";
import ProspectImport from "./pages/ProspectImport";
import SalesPerformance from "./pages/SalesPerformance";
import Clienti from "./pages/Clienti";
import ClienteDetail from "./pages/ClienteDetail";
import ClientiImport from "./pages/ClientiImport";
import PartnerCandidates from "./pages/PartnerCandidates";
import ReteConsulenze from "./pages/ReteConsulenze";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "partner_commerciale") return <Navigate to="/area-partner" replace />;
  return <Layout>{children}</Layout>;
}

function PartnerProtected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "partner_commerciale") return <Navigate to="/" replace />;
  return children;
}

function ProtectedBare({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleGate({ roles, children }) {
  const { user } = useAuth();
  if (user && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <RoutedErrorBoundary>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
            <Route path="/leads" element={<Protected><Leads /></Protected>} />
            <Route path="/progetti" element={<Protected><Projects /></Protected>} />
            <Route path="/tools" element={<Protected><Tools /></Protected>} />
            <Route path="/consulenze-tecniche" element={<Protected><ConsulenzeTecniche /></Protected>} />
            <Route path="/leads/:id" element={<Protected><LeadDetail /></Protected>} />
            <Route path="/leads/:id/print" element={<ProtectedBare><OutlookPrint /></ProtectedBare>} />
            <Route path="/leads/:id/contratto" element={<ProtectedBare><OutlookPrint contractMode /></ProtectedBare>} />
            <Route path="/leads/:id/subappalto" element={<ProtectedBare><SubappaltoDocument /></ProtectedBare>} />
            <Route path="/payment/success" element={<ProtectedBare><PaymentResult mode="success" /></ProtectedBare>} />
            <Route path="/payment/cancel" element={<ProtectedBare><PaymentResult mode="cancel" /></ProtectedBare>} />
            <Route path="/products" element={<Protected><RoleGate roles={["admin", "partner"]}><Products /></RoleGate></Protected>} />
            <Route path="/listino" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><Listino /></RoleGate></Protected>} />
            <Route path="/progettazione" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><Progettazione /></RoleGate></Protected>} />
            <Route path="/costi-base" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><CostiBase /></RoleGate></Protected>} />
            <Route path="/inverter" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><InverterCosts /></RoleGate></Protected>} />
            <Route path="/manodopera" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><ManodoperaCosts /></RoleGate></Protected>} />
            <Route path="/sicurezza" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><SicurezzaCosts /></RoleGate></Protected>} />
            <Route path="/margini" element={<Protected><RoleGate roles={["admin"]}><MarginiCosts /></RoleGate></Protected>} />
            <Route path="/manutenzioni" element={<Protected><RoleGate roles={["admin", "partner"]}><Manutenzioni /></RoleGate></Protected>} />
            <Route path="/archivio-bollette" element={<Protected><RoleGate roles={["admin"]}><ArchivioBollette /></RoleGate></Protected>} />
            <Route path="/motore-bolletta" element={<Protected><RoleGate roles={["admin"]}><MotoreBolletta /></RoleGate></Protected>} />
            <Route path="/preventivatore" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><Preventivatore /></RoleGate></Protected>} />
            <Route path="/preventivi" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><FierPreventivi /></RoleGate></Protected>} />
            <Route path="/ordini" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><Orders /></RoleGate></Protected>} />
            <Route path="/servizi" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><ListinoServizi /></RoleGate></Protected>} />
            <Route path="/condizioni-vendita" element={<Protected><RoleGate roles={["admin", "partner"]}><CondizioniVendita /></RoleGate></Protected>} />
            <Route path="/condizioni" element={<Protected><RoleGate roles={["admin", "partner"]}><Condizioni /></RoleGate></Protected>} />
            <Route path="/pagamenti" element={<Protected><RoleGate roles={["admin", "partner"]}><Pagamenti /></RoleGate></Protected>} />
            <Route path="/bonus-fiscali" element={<Protected><RoleGate roles={["admin", "partner"]}><BonusFiscali /></RoleGate></Protected>} />
            <Route path="/caparra" element={<Protected><RoleGate roles={["admin", "partner"]}><Caparra /></RoleGate></Protected>} />
            <Route path="/manutenzione" element={<Protected><RoleGate roles={["admin", "partner"]}><Manutenzione /></RoleGate></Protected>} />
            <Route path="/sopralluoghi" element={<Protected><RoleGate roles={["admin", "partner", "team_leader"]}><SopralluoghiSettings /></RoleGate></Protected>} />
            <Route path="/obiettivi-prospect" element={<Protected><RoleGate roles={["admin", "partner"]}><ObiettiviProspect /></RoleGate></Protected>} />
            <Route path="/sales/performance" element={<Protected><RoleGate roles={["admin", "partner", "sales", "qualification", "team_leader"]}><SalesPerformance /></RoleGate></Protected>} />
            <Route path="/email" element={<Protected><RoleGate roles={["admin", "partner"]}><EmailTemplates /></RoleGate></Protected>} />
            <Route path="/immagini-azienda" element={<Protected><RoleGate roles={["admin", "partner"]}><CompanyPhotos /></RoleGate></Protected>} />
            <Route path="/automazioni-crm" element={<Protected><RoleGate roles={["admin", "partner"]}><AutomazioniCRM /></RoleGate></Protected>} />
            <Route path="/coda-email" element={<Protected><RoleGate roles={["admin", "partner"]}><EmailQueue /></RoleGate></Protected>} />
            <Route path="/provvigioni" element={<Protected><RoleGate roles={["admin", "partner"]}><PartnerCommissions /></RoleGate></Protected>} />
            <Route path="/installatori-partner" element={<Protected><RoleGate roles={["admin", "partner"]}><PartnerInstallers /></RoleGate></Protected>} />
            <Route path="/agenzie-abilitate" element={<Protected><RoleGate roles={["admin", "partner"]}><AgenzieAbilitate /></RoleGate></Protected>} />
            <Route path="/bacheca" element={<Protected><RoleGate roles={["admin", "partner"]}><HeadPartnerBoard /></RoleGate></Protected>} />
            <Route path="/area-free" element={<Protected><RoleGate roles={["admin", "partner", "qualification", "sales", "team_leader"]}><FreeUsers /></RoleGate></Protected>} />
            <Route path="/testi-legali" element={<Protected><RoleGate roles={["admin", "partner"]}><LegalTexts /></RoleGate></Protected>} />
            <Route path="/articoli-subappalto" element={<Protected><RoleGate roles={["admin", "partner"]}><SubappaltoArticoli /></RoleGate></Protected>} />
            <Route path="/appuntamenti" element={<Protected><RoleGate roles={["admin", "partner", "team_leader"]}><Appointments /></RoleGate></Protected>} />
            <Route path="/calendario" element={<Protected><Agenda /></Protected>} />
            <Route path="/agenda" element={<Navigate to="/calendario" replace />} />
            <Route path="/profilo" element={<Protected><Profile /></Protected>} />
            <Route path="/settings" element={<Protected><RoleGate roles={["admin", "partner"]}><Settings /></RoleGate></Protected>} />
            <Route path="/stripe-test" element={<Protected><StripeTest /></Protected>} />
            <Route path="/qualita-crm" element={<Protected><RoleGate roles={["admin", "partner", "team_leader"]}><QualitaCRM /></RoleGate></Protected>} />
            <Route path="/prospects" element={<Protected><Prospects /></Protected>} />
            <Route path="/prospects/importa" element={<Protected><ProspectImport /></Protected>} />
            <Route path="/prospects/dashboard" element={<Protected><ProspectDashboard /></Protected>} />
            <Route path="/prospects/pipeline" element={<Protected><PipelineProspect /></Protected>} />
            <Route path="/prospects/:id" element={<Protected><ProspectDetail /></Protected>} />
            <Route path="/clienti" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><Clienti /></RoleGate></Protected>} />
            <Route path="/clienti/importa" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><ClientiImport /></RoleGate></Protected>} />
            <Route path="/clienti/:id" element={<Protected><RoleGate roles={["admin", "partner", "sales", "team_leader"]}><ClienteDetail /></RoleGate></Protected>} />
            <Route path="/candidati-partner" element={<Protected><RoleGate roles={["admin", "partner"]}><PartnerCandidates /></RoleGate></Protected>} />
            <Route path="/candidati-partner/:id" element={<Protected><RoleGate roles={["admin", "partner"]}><PartnerCandidates /></RoleGate></Protected>} />
            <Route path="/consulenze-rete" element={<Protected><RoleGate roles={["admin", "partner"]}><ReteConsulenze /></RoleGate></Protected>} />
            <Route path="/area-partner" element={<PartnerProtected><AreaPartner /></PartnerProtected>} />
            <Route path="/area-partner/:id" element={<PartnerProtected><AreaPartnerLead /></PartnerProtected>} />
            <Route path="/p/:token" element={<ProtectedContent><PublicOutlook /></ProtectedContent>} />
            <Route path="/p/:token/print" element={<OutlookPrint publicMode />} />
            <Route path="/p/:token/contratto" element={<OutlookPrint publicMode contractMode />} />
            <Route path="/c/:token" element={<ProtectedContent><PublicConsulenza /></ProtectedContent>} />
            <Route path="/configura" element={<ProtectedContent><ConfiguraWizard /></ProtectedContent>} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/mappa-installatori" element={<PublicInstallersMap />} />
            <Route path="/rete" element={<ProtectedContent><RetePage /></ProtectedContent>} />
            <Route path="/rete/p/:token" element={<ProtectedContent><RetePratica /></ProtectedContent>} />
            <Route path="/free/bolletta" element={<ProtectedContent><FreeBolletta /></ProtectedContent>} />
            <Route path="/free/tetto" element={<ProtectedContent><FreeTetto /></ProtectedContent>} />
            <Route path="/free/colonnina" element={<ProtectedContent><FreeColonnina /></ProtectedContent>} />
            <Route path="/free/colonnina/:token/print" element={<FreeColonnina printMode />} />
            <Route path="/c/:token/print" element={<OutlookPrint publicMode consulenzaMode />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </RoutedErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
